/**
 * GM reveal. A small IntersectionObserver utility for the gm- sections.
 *
 * Usage: wrap section content in a <gm-reveal-scope> element and put the
 * class "gm-reveal" on any descendant that should rise and fade in. The host
 * section owns the presentation in its own stylesheet: the hidden state
 * (opacity 0, translateY(16px)), the transition, and any nth-child stagger
 * delays. That CSS must be gated behind both the scope's :defined state and
 * a (prefers-reduced-motion: no-preference) media query, so content is never
 * hidden when JavaScript is unavailable and never animated when the visitor
 * has asked for reduced motion.
 *
 * Behavior:
 * - Each .gm-reveal descendant receives the class "is-visible" once it
 *   enters the viewport.
 * - Every element is unobserved the moment it fires. The reveal runs once.
 * - Under prefers-reduced-motion, or when IntersectionObserver is missing,
 *   everything is marked visible immediately and no observer is created.
 * - Extends Component so morphs via the Section Rendering API re-run setup
 *   through updatedCallback, and the theme editor re-initializes the scope
 *   automatically through connectedCallback.
 */

import { Component } from '@theme/component';

const REVEAL_SELECTOR = '.gm-reveal';
const VISIBLE_CLASS = 'is-visible';

class GmRevealScope extends Component {
  /** @type {IntersectionObserver | null} */
  #observer = null;

  connectedCallback() {
    super.connectedCallback();
    this.#setup();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#setup();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#teardown();
  }

  /**
   * Idempotent: safe to call again after a morph replaces children. Already
   * revealed elements are skipped and re-observing an element is a no-op.
   */
  #setup() {
    const targets = this.querySelectorAll(REVEAL_SELECTOR);
    if (targets.length === 0) return;

    const reduceMotion =
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (reduceMotion || !('IntersectionObserver' in window)) {
      // Hard opt-out: show everything, fully static, no observer at all.
      for (const target of targets) {
        target.classList.add(VISIBLE_CLASS);
      }
      return;
    }

    if (this.#observer === null) {
      this.#observer = new IntersectionObserver(this.#onIntersect, {
        rootMargin: '0px 0px -10% 0px',
        threshold: 0,
      });
    }

    for (const target of targets) {
      if (target.classList.contains(VISIBLE_CLASS)) continue;
      this.#observer.observe(target);
    }
  }

  #teardown() {
    if (this.#observer !== null) {
      this.#observer.disconnect();
      this.#observer = null;
    }
  }

  /**
   * @param {IntersectionObserverEntry[]} entries
   */
  #onIntersect = (entries) => {
    for (const entry of entries) {
      if (!entry.isIntersecting) continue;
      entry.target.classList.add(VISIBLE_CLASS);
      if (this.#observer !== null) {
        this.#observer.unobserve(entry.target);
      }
    }
  };
}

if (!customElements.get('gm-reveal-scope')) {
  customElements.define('gm-reveal-scope', GmRevealScope);
}
